import { Ban } from 'lucide-react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { PunchupButton } from 'features/common/components/Punchup/primitives/buttons/PunchupButton';
import {
	PunchupDialogue,
	DialogIcon,
	DialogTitle,
	ButtonGroup,
} from 'features/common/components/Punchup/composites/dialogs/PunchupDialogue';
import { PunchupDetailText } from 'features/common/components/Punchup/composites/content/PunchupDetailText';
import styles from './modals.module.css';

interface VoidModalProps {
	reason: string;
	onReasonChange: (reason: string) => void;
	onConfirm: () => void;
	onClose: () => void;
	isLoading: boolean;
}

/** Voids an imported ticket (TIC-952) — the import's stand-in for Refund. The reason is required (audit log). */
export const VoidModal: FC<VoidModalProps> = ({ reason, onReasonChange, onConfirm, onClose, isLoading }) => {
	const { t } = useTranslation();
	const hasReason = reason.trim().length > 0;
	return (
		<PunchupDialogue maxWidth={400} isLoading={isLoading} isDestructive onClose={onClose}>
			<DialogIcon variant="destructive">
				<Ban size={24} />
			</DialogIcon>
			<DialogTitle>{t('attendee-actions-void-ticket', 'Void Ticket')}</DialogTitle>
			<PunchupDetailText
				main={t(
					'attendee-actions-void-main',
					'This imported ticket will be voided and the attendee will lose access to the event.'
				)}
				secondary={t(
					'attendee-actions-void-secondary',
					'Imported tickets were paid on another platform, so Punchup cannot refund them — any refund is handled where the ticket was sold. Un-Cancel Ticket restores the ticket, but not add-ons released when the order’s last ticket is voided.'
				)}
				collapsible
			/>
			<textarea
				className={styles.textarea}
				aria-label={t('attendee-actions-void-reason-label', 'Reason for voiding')}
				placeholder={t('attendee-actions-void-reason', 'Reason for voiding (required)')}
				value={reason}
				onChange={(e) => onReasonChange(e.target.value)}
			/>
			<ButtonGroup>
				<PunchupButton variant="secondary" text={t('cancel', 'Cancel')} onClick={onClose} disabled={isLoading} />
				<PunchupButton
					variant="destructive"
					text={t('attendee-actions-void-confirm', 'Void ticket')}
					onClick={onConfirm}
					disabled={isLoading || !hasReason}
				/>
			</ButtonGroup>
		</PunchupDialogue>
	);
};
